import { type ConsentState } from '../../typings'
import { BaseCMP } from './base-cmp'

export class CookieScriptCMP extends BaseCMP {
  private listenersInitialized = false

  initialize(): void {
    if (this.initialized) {
      return
    }

    const managementMode = this.getManagementMode()

    if (managementMode === 'managed') {
      this.installCookieScript()
    } else if (managementMode === 'self') {
      this.log('CookieScript assumed to be already installed, setting up listeners')
      this.setupEventListeners()
    }

    this.initialized = true
  }

  private installCookieScript(): void {
    if (!this.config.cmpId) {
      throw new Error('CookieScript CMP ID is required for installation')
    }

    const loadCookieScript = () => {
      if (!document.getElementById('cookie-script')) {
        const script = document.createElement('script')
        script.id = 'cookie-script'
        script.type = 'text/javascript'
        script.charset = 'UTF-8'
        script.src = `//cdn.cookie-script.com/s/${this.config.cmpId}.js`
        script.async = true

        script.onerror = (e) => {
          this.logError('Failed to load CookieScript script', e)
        }

        document.head.appendChild(script)
      }

      this.setupEventListeners()
    }

    // Load now rather than on `load`: the consent answer gates every tag, and
    // `wait_for_update` is only 500ms. Waiting for every image to finish means a
    // returning visitor who already consented still gets a `denied` first hit.
    loadCookieScript()
  }

  protected setupEventListeners(): void {
    if (this.listenersInitialized) return

    window.addEventListener('CookieScriptLoaded', this.handleCookieScriptLoaded)
    window.addEventListener('CookieScriptAccept', this.handleCookieScriptAccept)
    window.addEventListener('CookieScriptAcceptAll', this.handleCookieScriptAcceptAll)
    window.addEventListener('CookieScriptReject', this.handleCookieScriptReject)
    window.addEventListener('CookieScriptClose', this.handleCookieScriptClose)

    this.listenersInitialized = true
    this.log('CookieScript event listeners initialized')
  }

  private handleCookieScriptLoaded = () => {
    this.log('CookieScript loaded')
    this.handleConsentUpdate()
  }

  private handleCookieScriptAccept = (event: Event) => {
    this.log('CookieScript accept event', (event as CustomEvent).detail)
    this.handleConsentUpdate()
  }

  private handleCookieScriptAcceptAll = () => {
    this.log('CookieScript accept all event')
    this.handleConsentUpdate({
      analytics: true,
      marketing: true,
      preferences: true,
      client_timestamp: Date.now()
    })
  }

  private handleCookieScriptReject = () => {
    this.log('CookieScript reject event')
    this.handleConsentUpdate()
  }

  private handleCookieScriptClose = () => {
    this.log('CookieScript close event')
    this.handleConsentUpdate()
  }

  protected handleConsentUpdate(consentState?: ConsentState | null): void {
    const newConsentState = consentState ?? this.getConsentState()
    if (newConsentState && this.consentUpdateCallback) {
      this.consentUpdateCallback(newConsentState)
    }
  }

  getConsentState(): ConsentState | null {
    // Check if CookieScript instance is available
    const cookieScript = (window as any).CookieScript?.instance
    if (!cookieScript) {
      return null
    }

    try {
      const currentState = cookieScript.currentState()
      if (!currentState) {
        return null
      }

      // Map CookieScript categories to our consent categories
      const acceptedCategories = currentState.categories || []
      const analytics = acceptedCategories.includes('performance')
      const marketing = acceptedCategories.includes('targeting')
      const preferences = acceptedCategories.includes('functionality')

      return {
        analytics,
        marketing,
        preferences,
        client_timestamp: Date.now()
      }
    } catch (error) {
      this.logError('Failed to get consent state from CookieScript', error)
      return null
    }
  }

  destroy(): void {
    window.removeEventListener('CookieScriptLoaded', this.handleCookieScriptLoaded)
    window.removeEventListener('CookieScriptAccept', this.handleCookieScriptAccept)
    window.removeEventListener('CookieScriptAcceptAll', this.handleCookieScriptAcceptAll)
    window.removeEventListener('CookieScriptReject', this.handleCookieScriptReject)
    window.removeEventListener('CookieScriptClose', this.handleCookieScriptClose)

    this.listenersInitialized = false
    this.initialized = false
    this.consentUpdateCallback = undefined

    this.log('Destroyed CookieScript CMP')
  }
}
